import { IDLE_LIMIT_MS, isIdleExpired, markActive, useSession } from "@/features/auth";
import { router } from "./router";

/**
 * The idle lock's wiring — design decision 7: fifteen minutes with no human input ends the
 * session on this tab and asks for the password again.
 *
 * `features/auth/idle-lock.ts` keeps the clock; this file feeds it real events and acts on it.
 * The same shape as `session-guard.ts`: one wiring call from `main.ts`, and the session ends
 * through `session.logout()` (token, cached queries, break-glass, idle clock all go) followed by
 * an explicit navigation to `/login` carrying `redirect` and `reason: "idle"`.
 *
 * **Checked on a coarse interval, not a precise timer reset on every event.** A tick every
 * `CHECK_EVERY_MS` compares "now" against the latest activity any tab has recorded; activity
 * itself is a cheap timestamp write. That keeps `pointermove` from rescheduling timers dozens of
 * times a second, and it makes cross-tab activity count for free (the clock is in storage).
 *
 * **A tab coming back into view is checked BEFORE it counts as activity.** A laptop lid closed for
 * an hour suspends timers; when it opens, `visibilitychange` fires before any tick. Treating that
 * event as activity first would refresh the clock and defeat the lock at exactly the moment it
 * exists for.
 */
export const CHECK_EVERY_MS = 10_000;

const ACTIVITY_EVENTS = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart"] as const;

function lockIfIdle(): boolean {
  const session = useSession();
  if (!session.isAuthed) return false;
  if (!isIdleExpired()) return false;
  const current = router.currentRoute.value;
  session.logout();
  void router.replace({
    path: "/login",
    query: { redirect: current.fullPath, reason: "idle" },
  });
  return true;
}

function onActivity(): void {
  if (!useSession().isAuthed) return;
  markActive();
}

function onVisibility(): void {
  if (document.visibilityState !== "visible") return;
  if (lockIfIdle()) return;
  onActivity();
}

/**
 * Call once, at startup. Returns a disposer for tests. The limit is fixed at `IDLE_LIMIT_MS`; it
 * is re-exported here only so a test can advance fake timers by it without a magic number.
 */
export function watchForIdle(): () => void {
  for (const e of ACTIVITY_EVENTS) window.addEventListener(e, onActivity, { passive: true });
  document.addEventListener("visibilitychange", onVisibility);
  const interval = window.setInterval(lockIfIdle, CHECK_EVERY_MS);
  return () => {
    for (const e of ACTIVITY_EVENTS) window.removeEventListener(e, onActivity);
    document.removeEventListener("visibilitychange", onVisibility);
    window.clearInterval(interval);
  };
}

export { IDLE_LIMIT_MS };
